<template>
  <div>
    <div class="mt-[20px] mb-2">
      <BaseElButton data-cy="add-period-btn" plain @click="addTimes">{{ displayText.title }}</BaseElButton>
    </div>
    <BaseElFormItem prop="excludeTimeRule" label-width="0">
      <GrayBlockContainer
        v-for="(item, index) in data"
        :key="index"
        class="flex flex-col mb-[20px] w-full"
      >
        <BaseElRadioGroup v-model="item.type" class="mb-[8px]">
          <BaseElRadio
            v-for="item in displayExcludePeriodOptions"
            :key="item.value"
            :label="item.value"
          >
            {{ item.label }}
          </BaseElRadio>
        </BaseElRadioGroup>
        <div class="flex flex-row items-center">
          <div class="w-full">
            <!--
              🔴 value-format 不可以拿掉（CNCY-10430）：沒設時 el-date-picker 的
              v-model 是 Date 物件，JSON.stringify 之後就成了帶 Z 的 ISO ⇒ 後端存到的是
              **操作者瀏覽器時區**的絕對時間戳，而不是商家心中的那組年月日時分。
              操作者與店家不同時區時（海外店家、總部跨時區操作），排除時段會整段錯開；
              顯示端同樣用瀏覽器時區印，所以畫面上永遠看起來是對的、沒有人會發現。
              後端 onceSetting 收的是店家牆上時間裸字串，格式與取貨的一次性關閉一致。
            -->
            <el-date-picker
              v-if="item.type === 'once'"
              v-model="item.onceSetting.dateRange"
              editable
              type="datetimerange"
              format="yyyy-MM-dd HH:mm"
              value-format="yyyy-MM-dd HH:mm:ss"
              range-separator="至"
              :start-placeholder="displayText.startDate"
              :end-placeholder="displayText.endDate"
              :default-time="['00:00:00', '23:59:59']"
              class="!w-full"
            />
            <div v-if="item.type === 'weekly'">
              <div class="grid grid-flow-row gap-[8px]">
                <div class="grid gap-[8px] grid-flow-col items-center">
                  <BaseElSelect
                    v-model="item.weeklySetting.week"
                    class="w-full"
                    :placeholder="displayText.selectWeek"
                  >
                    <BaseElSelectOption
                      v-for="week in weeksOptions"
                      :key="week.value"
                      :label="$t(week.label)"
                      :value="week.value"
                    />
                  </BaseElSelect>
                  <el-time-select v-model="item.weeklySetting.startTime" class="w-full" :picker-options="{ start: '00:00', step: '00:01', end: '23:59'}" />
                  <el-time-select v-model="item.weeklySetting.endTime" class="w-full" :picker-options="{ start: '00:00', step: '00:01', end: '23:59'}" />
                </div>
              </div>
            </div>
            <div v-if="item.type === 'monthly'">
              <div class="grid grid-flow-row gap-[8px]">
                <div class="grid gap-[8px] grid-flow-col items-center">
                  <p>{{ displayText.start }}</p>
                  <BaseElSelect
                    v-model="item.monthlySetting.startDay"
                    class="w-full"
                  >
                    <BaseElSelectOption
                      v-for="day in 31"
                      :key="day"
                      :label="`${day} ${displayText.day}`"
                      :value="day"
                    />
                  </BaseElSelect>
                  <el-time-select v-model="item.monthlySetting.startTime" class="w-full" :picker-options="{ start: '00:00', step: '00:01', end: '23:59'}" />
                </div>
                <div class="grid gap-[8px] grid-flow-col items-center">
                  <p>{{ displayText.end }}</p>
                  <BaseElSelect
                    v-model="item.monthlySetting.endDay"
                    class="w-full"
                  >
                    <BaseElSelectOption
                      v-for="day in 31"
                      :key="day"
                      :label="`${day} ${displayText.day}`"
                      :value="day"
                      :disabled="day < item.monthlySetting.startDay"
                    />
                  </BaseElSelect>
                  <el-time-select v-model="item.monthlySetting.endTime" :picker-options="{ start: '00:00', step: '00:01', end: '23:59'}" />
                </div>
              </div>
            </div>
            <div v-if="item.type === 'yearly'">
              <div class="grid grid-flow-row gap-[8px]">
                <div class="grid gap-[8px] grid-flow-col items-center">
                  <p>{{ displayText.start }}</p>
                  <BaseElSelect
                    v-model="item.yearlySetting.startMonth"
                    class="w-full"
                  >
                    <BaseElSelectOption
                      v-for="month in 12"
                      :key="month"
                      :label="`${month} ${displayText.month}`"
                      :value="month"
                    />
                  </BaseElSelect>
                  <BaseElSelect
                    v-model="item.yearlySetting.startDay"
                    class="w-full"
                  >
                    <BaseElSelectOption
                      v-for="day in 31"
                      :key="day"
                      :label="`${day} ${displayText.day}`"
                      :value="day"
                    />
                  </BaseElSelect>
                  <el-time-select v-model="item.yearlySetting.startTime" class="w-full" :picker-options="{ start: '00:00', step: '00:01', end: '23:59'}" />
                </div>
                <div class="grid gap-[8px] grid-flow-col items-center">
                  <p>{{ displayText.end }}</p>
                  <BaseElSelect
                    v-model="item.yearlySetting.endMonth"
                    class="w-full"
                  >
                    <BaseElSelectOption
                      v-for="month in 12"
                      :key="month"
                      :label="`${month} ${displayText.month}`"
                      :value="month"
                      :disabled="month < item.yearlySetting.startMonth"
                    />
                  </BaseElSelect>
                  <BaseElSelect
                    v-model="item.yearlySetting.endDay"
                    class="w-full"
                  >
                    <BaseElSelectOption
                      v-for="day in 31"
                      :key="day"
                      :label="`${day} ${displayText.day}`"
                      :value="day"
                      :disabled="item.yearlySetting.startMonth === item.yearlySetting.endMonth && day < item.yearlySetting.startDay"
                    />
                  </BaseElSelect>
                  <el-time-select v-model="item.yearlySetting.endTime" :picker-options="{ start: '00:00', step: '00:01', end: '23:59'}" />
                </div>
              </div>
            </div>
          </div>
          <div
            class="close p-2 ml-2 cursor-pointer"
            @click="remove(index)"
          >
            <SVGIcon
              icon="common-trash"
              fill="var(--gray-60)"
            />
          </div>
        </div>
        <!-- 🔴 CNCY-10468：monthly／yearly 的兩列選單是一段連續區間，商家很容易
             照「每個月的那幾天、每天這個時段」去設，設出整月關店也不會有任何提示。
             這裡把後端實際會排除的區間直接講出來。 -->
        <div v-if="previews[index]" class="mt-[8px]">
          <p class="text-sub">{{ previews[index].text }}</p>
          <p
            v-for="(note, noteIndex) in previews[index].notes"
            :key="noteIndex"
            class="text-sub text-danger"
          >
            {{ note }}
          </p>
        </div>
      </GrayBlockContainer>
    </BaseElFormItem>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue'
import { displayExcludePeriodConfig } from '@/config/reservation'
import GrayBlockContainer from '@/components/Container/GrayBlockContainer.vue'
import { weeksOptions } from '@/config/date'
import { describeExcludeTimeRule } from '@/utils/excludeTimeRule'
import { omit } from 'lodash'

export default defineComponent({
  name: 'AddPeriodTimes',
  components: { GrayBlockContainer },
  props: {
    title: {
      type: String,
      default: '新增時段',
    },
    excludeTimeRule: Array,
    omitList: {
      type: Array,
      default: () => [],
    },
  },
  emits: [],
  setup (props, { emit }) {
    const data = computed(() => props.excludeTimeRule)
    const emptyTime = {
      type: 'once', // Default to "不重複"
      onceSetting: {
        dateRange: [], // 店家牆上時間字串 YYYY-MM-DD HH:mm:ss（value-format）
        startAt: 0,
        endAt: 0,
      },
      weeklySetting: {
        week: 0, // 0-6
        startTime: 0, // HH * mm
        endTime: 0, // HH * mm
      },
      monthlySetting: {
        startDay: 1,
        startTime: 0, // HH * mm
        endDay: 1,
        endTime: 0, // HH * mm
      },
      yearlySetting: {
        startMonth: 1,
        startDay: 1,
        startTime: 0, // HH * mm
        endMonth: 1,
        endDay: 1,
        endTime: 0, // HH * mm
      },
    }
    const remove = (index) => {
      data.value.splice(index, 1)
    }
    const addTimes = () => {
      const times = data.value
      times.push(JSON.parse(JSON.stringify(emptyTime)))
    }
    // 逐條算一次就好——模板裡直接呼叫的話 text 與 notes 會各算一次
    const previews = computed(() => (data.value || []).map(describeExcludeTimeRule))

    const displayExcludePeriodOptions = computed(() => {
      return omit(displayExcludePeriodConfig, props.omitList)
    })

    const displayText = computed(() => {
      const data = {
        title: '新增時段',
        startDate: '開始日期',
        endDate: '結束日期',
        selectWeek: '請選擇星期',
        start: '起始',
        end: '結束',
        day: '日',
        month: '月',
      }
      return data
    })

    return {
      data,
      addTimes,
      remove,
      displayExcludePeriodOptions,
      weeksOptions,
      displayText,
      previews,
    }
  },
})
</script>

<style lang="postcss" scoped>
::v-deep .el-date-editor.el-input {
  @apply w-full
}
::v-deep .el-radio-group .el-radio__label {
  @apply text-sub
}
::v-deep .el-input {
  @apply w-auto
}
</style>
